import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, ChevronDown, ChevronRight, GripVertical, ArrowUp, ArrowDown, X, Save, FolderPlus, Film, Lock, Crown, ImagePlus, Loader2, Check } from 'lucide-react';
import api from '../../lib/api';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Spinner } from '../ui/Spinner';
import { LessonEditor } from './LessonEditor';

function CourseModal({ course, onClose, onSaved }) {
  const isEdit = !!course;
  const [name, setName] = useState(course?.name || '');
  const [description, setDescription] = useState(course?.description || '');
  const [cover, setCover] = useState(course?.cover_url || '');
  const [coverKey, setCoverKey] = useState(course?.cover_key || null);
  const [preview, setPreview] = useState(course?.cover || course?.cover_url || '');
  const [uploading, setUploading] = useState(false);
  const [mentorship, setMentorship] = useState(!!course?.mentorship);
  const [busy, setBusy] = useState(false);

  const onCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Please choose an image file');
    setUploading(true);
    try {
      const { data } = await api.post('/admin/r2/presign-upload', {
        filename: file.name, content_type: file.type || 'image/png', folder: 'course-covers',
      });
      await fetch(data.put_url, { method: 'PUT', body: file, headers: { 'Content-Type': data.content_type || 'image/png' } });
      setCoverKey(data.key);
      setPreview(data.get_url);
      setCover('');
      toast.success('Cover uploaded');
    } catch (er) {
      if (er?.response?.status === 501) toast.error('Image upload isn\u2019t available (Cloudflare R2 not connected). Paste a URL instead.');
      else toast.error('Could not upload image');
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Course name is required');
    setBusy(true);
    const payload = { name: name.trim(), description, cover_url: cover, cover_key: coverKey, mentorship };
    try {
      if (isEdit) await api.put(`/admin/courses/${course.course_id}`, payload);
      else await api.post('/admin/courses', payload);
      toast.success(isEdit ? 'Course updated' : 'Course created');
      onSaved?.(); onClose?.();
    } catch (er) { toast.error(er?.response?.data?.detail || 'Could not save course'); }
    finally { setBusy(false); }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto p-4 sm:p-8">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <motion.form onSubmit={submit} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}
          className="relative z-10 w-full max-w-lg rounded-2xl border border-white/10 bg-ink-card p-6" data-testid="course-editor">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold text-white">{isEdit ? 'Edit course' : 'New course'}</h3>
            <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white"><X size={20} /></button>
          </div>
          <div className="space-y-3">
            <div><label className="mb-1 block text-xs text-zinc-400">Name</label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="course-name" /></div>
            <div><label className="mb-1 block text-xs text-zinc-400">Description</label><Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="course-description" /></div>
            <div>
              <label className="mb-1 block text-xs text-zinc-400">Cover image</label>
              <div className="flex items-center gap-3">
                <div className="h-16 w-28 shrink-0 overflow-hidden rounded-lg border border-white/10 bg-ink-surface">
                  {preview ? (
                    <img src={preview} alt="Cover preview" className="h-full w-full object-cover" data-testid="course-cover-preview" />
                  ) : (
                    <div className="grid h-full place-items-center text-zinc-600"><ImagePlus size={18} /></div>
                  )}
                </div>
                <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-zinc-700 px-4 py-2.5 text-sm text-zinc-300 hover:border-zinc-500" data-testid="course-cover-upload-label">
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : coverKey ? <Check size={16} className="text-emerald" /> : <ImagePlus size={16} />}
                  {uploading ? 'Uploading…' : coverKey ? 'Replace image' : 'Upload image'}
                  <input type="file" accept="image/*" className="hidden" onChange={onCoverFile} data-testid="course-cover-upload" />
                </label>
              </div>
              <label className="mb-1 mt-3 block text-xs text-zinc-400">…or paste an image URL</label>
              <Input value={cover} onChange={(e) => { setCover(e.target.value); setCoverKey(null); setPreview(e.target.value); }} placeholder="https://…" data-testid="course-cover" />
            </div>
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-ink-surface p-3" data-testid="course-mentorship-label">
              <input type="checkbox" checked={mentorship} onChange={(e) => setMentorship(e.target.checked)} className="mt-0.5 h-4 w-4 accent-emerald" data-testid="course-mentorship-toggle" />
              <span>
                <span className="flex items-center gap-1.5 text-sm font-medium text-white"><Crown size={14} className="text-emerald" /> Mentorship course</span>
                <span className="mt-0.5 block text-xs text-zinc-500">Shows only in the Mentorship tab and is locked to members with the Mentorship Whop role. Hidden from the regular Courses tab.</span>
              </span>
            </label>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="emerald" disabled={busy} data-testid="course-save"><Save size={16} /> {busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </motion.form>
      </div>
    </AnimatePresence>
  );
}

function LessonRow({ lesson, onEdit, onUp, onDown, onUnassign, canUp, canDown, onDragStart, onDragOver, onDrop, dragging }) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`flex items-center gap-2 rounded-lg border bg-ink-surface px-3 py-2 transition-colors ${dragging ? 'border-emerald/60 opacity-50' : 'border-white/5'}`}
      data-testid={`course-lesson-${lesson.lesson_id}`}
    >
      <span className="cursor-grab text-zinc-600 hover:text-emerald active:cursor-grabbing" title="Drag to reorder"><GripVertical size={15} /></span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-white">{lesson.title}</div>
        <div className="mt-0.5 flex items-center gap-1.5">
          {lesson.bunny_video_id || lesson.cloudflare_stream_id || lesson.video_url ? <Badge tone="emerald">Video</Badge> : <Badge tone="zinc">No video</Badge>}
          {lesson.status === 'draft' && <span className="text-[11px] text-zinc-500">Draft</span>}
        </div>
      </div>
      <button onClick={onUp} disabled={!canUp} className="text-zinc-500 hover:text-white disabled:opacity-30" data-testid={`lesson-up-${lesson.lesson_id}`}><ArrowUp size={15} /></button>
      <button onClick={onDown} disabled={!canDown} className="text-zinc-500 hover:text-white disabled:opacity-30" data-testid={`lesson-down-${lesson.lesson_id}`}><ArrowDown size={15} /></button>
      <button onClick={onEdit} className="text-zinc-400 hover:text-emerald" data-testid={`lesson-edit-${lesson.lesson_id}`}><Pencil size={15} /></button>
      <button onClick={onUnassign} className="text-zinc-500 hover:text-red-400" title="Remove from section" data-testid={`lesson-unassign-${lesson.lesson_id}`}><X size={15} /></button>
    </div>
  );
}

export function AdminCourses({ courses, reloadCourses }) {
  const [expanded, setExpanded] = useState(null);
  const [sections, setSections] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [newSection, setNewSection] = useState('');
  const [dragId, setDragId] = useState(null);
  const [dragSection, setDragSection] = useState(null);
  const [courseModal, setCourseModal] = useState(null); // {course} or {} for new
  const [lessonModal, setLessonModal] = useState(null);  // {lesson?, course_id, section_id}

  const loadDetail = useCallback(async (courseId) => {
    if (!courseId) return;
    setLoadingDetail(true);
    try {
      const [s, l] = await Promise.all([
        api.get(`/admin/courses/${courseId}/sections`).then((r) => r.data.sections || []),
        api.get('/admin/lessons', { params: { course_id: courseId } }).then((r) => r.data.lessons || []),
      ]);
      setSections(s); setLessons(l);
    } finally { setLoadingDetail(false); }
  }, []);

  useEffect(() => { if (expanded) loadDetail(expanded); }, [expanded, loadDetail]);

  const toggle = (id) => setExpanded((cur) => (cur === id ? null : id));

  const deleteCourse = async (id) => {
    if (!window.confirm('Delete this course? Its lessons stay but get unassigned.')) return;
    try { await api.delete(`/admin/courses/${id}`); toast.success('Course deleted'); if (expanded === id) setExpanded(null); reloadCourses(); }
    catch { toast.error('Could not delete course'); }
  };

  const addSection = async (courseId) => {
    if (!newSection.trim()) return;
    try { await api.post('/admin/sections', { course_id: courseId, name: newSection.trim() }); setNewSection(''); loadDetail(courseId); reloadCourses(); }
    catch { toast.error('Could not add section'); }
  };
  const renameSection = async (s) => {
    const name = window.prompt('Section name:', s.name);
    if (!name || !name.trim()) return;
    try { await api.put(`/admin/sections/${s.section_id}`, { name: name.trim() }); loadDetail(expanded); }
    catch { toast.error('Could not rename'); }
  };
  const deleteSection = async (s) => {
    if (!window.confirm(`Delete section "${s.name}"? Its lessons stay in the course, unsectioned.`)) return;
    try { await api.delete(`/admin/sections/${s.section_id}`); loadDetail(expanded); reloadCourses(); }
    catch { toast.error('Could not delete'); }
  };
  const reorderSections = async (idx, dir) => {
    const arr = [...sections];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setSections(arr);
    try { await api.put(`/admin/courses/${expanded}/sections/reorder`, { ids: arr.map((s) => s.section_id) }); }
    catch { toast.error('Could not reorder'); loadDetail(expanded); }
  };

  const lessonsInSection = (sid) => lessons
    .filter((l) => (l.section_id || '') === sid)
    .sort((a, b) => (a.section_order || 0) - (b.section_order || 0));
  const unsectioned = lessons
    .filter((l) => !l.section_id)
    .sort((a, b) => (a.section_order || 0) - (b.section_order || 0));

  const reorderLessons = async (sid, idx, dir) => {
    const arr = lessonsInSection(sid);
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try { await api.put(`/admin/sections/${sid}/lessons/reorder`, { ids: arr.map((l) => l.lesson_id) }); loadDetail(expanded); }
    catch { toast.error('Could not reorder lessons'); }
  };
  const handleLessonDrop = async (targetLid, sid) => {
    const src = dragId;
    setDragId(null);
    if (!src || src === targetLid || dragSection !== sid) return;
    const ids = lessonsInSection(sid).map((l) => l.lesson_id);
    const from = ids.indexOf(src);
    const to = ids.indexOf(targetLid);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    try { await api.put(`/admin/sections/${sid}/lessons/reorder`, { ids }); loadDetail(expanded); }
    catch { toast.error('Could not reorder lessons'); }
  };
  const unassignLesson = async (lessonId) => {
    try { await api.put(`/admin/lessons/${lessonId}`, { section_id: '' }); loadDetail(expanded); }
    catch { toast.error('Could not update lesson'); }
  };
  const assignToSection = async (lessonId, sid) => {
    try { await api.put(`/admin/lessons/${lessonId}`, { section_id: sid }); loadDetail(expanded); }
    catch { toast.error('Could not assign'); }
  };
  const reorderUnsectioned = async (idx, dir) => {
    const arr = [...unsectioned];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try { await api.put(`/admin/courses/${expanded}/lessons/reorder`, { ids: arr.map((l) => l.lesson_id) }); loadDetail(expanded); }
    catch { toast.error('Could not reorder lessons'); }
  };
  const handleUnsectionedDrop = async (targetLid) => {
    const src = dragId;
    setDragId(null);
    if (!src || src === targetLid || dragSection !== '__unsectioned__') return;
    const ids = unsectioned.map((l) => l.lesson_id);
    const from = ids.indexOf(src);
    const to = ids.indexOf(targetLid);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    try { await api.put(`/admin/courses/${expanded}/lessons/reorder`, { ids }); loadDetail(expanded); }
    catch { toast.error('Could not reorder lessons'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-courses">
      <div className="flex justify-end">
        <Button variant="emerald" onClick={() => setCourseModal({})} data-testid="course-new"><Plus size={16} /> New course</Button>
      </div>

      {courses.length === 0 && <Card className="p-8 text-center text-sm text-zinc-500">No courses yet. Create your first course to organize lessons.</Card>}

      {courses.map((c) => (
        <Card key={c.course_id} className="overflow-hidden" data-testid={`course-${c.course_id}`}>
          <div className="flex items-center gap-3 p-4">
            <button onClick={() => toggle(c.course_id)} className="text-zinc-400 hover:text-white" data-testid={`course-toggle-${c.course_id}`}>
              {expanded === c.course_id ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate font-heading font-semibold text-white">{c.name}</span>
                {c.mentorship && <Badge tone="emerald"><Crown size={11} className="mr-0.5 inline" /> Mentorship</Badge>}
              </div>
              <div className="text-xs text-zinc-500">{c.section_count || 0} sections · {c.lesson_count || 0} lessons</div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setCourseModal({ course: c })} data-testid={`course-edit-${c.course_id}`}><Pencil size={14} /> Edit</Button>
            <button onClick={() => deleteCourse(c.course_id)} className="text-zinc-500 hover:text-red-400" data-testid={`course-delete-${c.course_id}`}><Trash2 size={16} /></button>
          </div>

          {expanded === c.course_id && (
            <div className="border-t border-white/5 bg-ink-surface/40 p-4">
              {loadingDetail ? <div className="grid h-24 place-items-center"><Spinner className="h-6 w-6" /></div> : (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[200px]">
                      <label className="mb-1 block text-xs text-zinc-400">Add section</label>
                      <Input value={newSection} onChange={(e) => setNewSection(e.target.value)} placeholder="e.g. Module 1: Foundations" data-testid="section-name" />
                    </div>
                    <Button variant="outline" onClick={() => addSection(c.course_id)} data-testid="section-add"><FolderPlus size={16} /> Add</Button>
                    <Button variant="emerald" onClick={() => setLessonModal({ course_id: c.course_id, section_id: '' })} data-testid="course-new-lesson"><Film size={16} /> New lesson</Button>
                  </div>

                  {sections.map((s, si) => (
                    <div key={s.section_id} className="rounded-xl border border-white/10 bg-ink-card p-3" data-testid={`section-${s.section_id}`}>
                      <div className="mb-2 flex items-center gap-2">
                        <span className="flex-1 text-sm font-semibold text-white">{s.name}</span>
                        <button onClick={() => reorderSections(si, -1)} disabled={si === 0} className="text-zinc-500 hover:text-white disabled:opacity-30" data-testid={`section-up-${s.section_id}`}><ArrowUp size={15} /></button>
                        <button onClick={() => reorderSections(si, 1)} disabled={si === sections.length - 1} className="text-zinc-500 hover:text-white disabled:opacity-30" data-testid={`section-down-${s.section_id}`}><ArrowDown size={15} /></button>
                        <button onClick={() => renameSection(s)} className="text-zinc-400 hover:text-emerald" data-testid={`section-rename-${s.section_id}`}><Pencil size={14} /></button>
                        <button onClick={() => deleteSection(s)} className="text-zinc-500 hover:text-red-400" data-testid={`section-delete-${s.section_id}`}><Trash2 size={15} /></button>
                        <Button size="sm" variant="ghost" onClick={() => setLessonModal({ course_id: c.course_id, section_id: s.section_id })} data-testid={`section-add-lesson-${s.section_id}`}><Plus size={14} /> Lesson</Button>
                      </div>
                      <div className="space-y-1.5">
                        {lessonsInSection(s.section_id).map((l, li, arr) => (
                          <LessonRow key={l.lesson_id} lesson={l}
                            canUp={li > 0} canDown={li < arr.length - 1}
                            onUp={() => reorderLessons(s.section_id, li, -1)}
                            onDown={() => reorderLessons(s.section_id, li, 1)}
                            onEdit={() => setLessonModal({ lesson: l, course_id: c.course_id, section_id: s.section_id })}
                            onUnassign={() => unassignLesson(l.lesson_id)}
                            dragging={dragId === l.lesson_id}
                            onDragStart={() => { setDragId(l.lesson_id); setDragSection(s.section_id); }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => handleLessonDrop(l.lesson_id, s.section_id)} />
                        ))}
                        {lessonsInSection(s.section_id).length === 0 && <p className="px-1 py-2 text-xs text-zinc-600">No lessons in this section.</p>}
                      </div>
                    </div>
                  ))}

                  <div className="rounded-xl border border-dashed border-white/10 p-3">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Course lessons {sections.length > 0 && '(unsectioned)'}</div>
                    <div className="space-y-1.5">
                      {unsectioned.map((l, li, arr) => (
                        <div
                          key={l.lesson_id}
                          draggable
                          onDragStart={() => { setDragId(l.lesson_id); setDragSection('__unsectioned__'); }}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => handleUnsectionedDrop(l.lesson_id)}
                          className={`flex items-center gap-2 rounded-lg border bg-ink-surface px-3 py-2 transition-colors ${dragId === l.lesson_id ? 'border-emerald/60 opacity-50' : 'border-white/5'}`}
                          data-testid={`unsectioned-${l.lesson_id}`}
                        >
                          <span className="cursor-grab text-zinc-600 hover:text-emerald active:cursor-grabbing" title="Drag to reorder"><GripVertical size={15} /></span>
                          <div className="min-w-0 flex-1 truncate text-sm text-white">{l.title}</div>
                          <button onClick={() => reorderUnsectioned(li, -1)} disabled={li === 0} className="text-zinc-500 hover:text-white disabled:opacity-30" data-testid={`unsectioned-up-${l.lesson_id}`}><ArrowUp size={15} /></button>
                          <button onClick={() => reorderUnsectioned(li, 1)} disabled={li === arr.length - 1} className="text-zinc-500 hover:text-white disabled:opacity-30" data-testid={`unsectioned-down-${l.lesson_id}`}><ArrowDown size={15} /></button>
                          {sections.length > 0 && (
                            <select defaultValue="" onChange={(e) => e.target.value && assignToSection(l.lesson_id, e.target.value)}
                              className="h-8 rounded-lg border border-zinc-800 bg-ink-card px-2 text-xs text-white" data-testid={`assign-${l.lesson_id}`}>
                              <option value="">Move to…</option>
                              {sections.map((s) => <option key={s.section_id} value={s.section_id}>{s.name}</option>)}
                            </select>
                          )}
                          <button onClick={() => setLessonModal({ lesson: l, course_id: c.course_id, section_id: '' })} className="text-zinc-400 hover:text-emerald"><Pencil size={15} /></button>
                        </div>
                      ))}
                      {unsectioned.length === 0 && <p className="px-1 py-1 text-xs text-zinc-600">All course lessons are in a section.</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      ))}

      {courseModal && <CourseModal course={courseModal.course} onClose={() => setCourseModal(null)} onSaved={reloadCourses} />}
      {lessonModal && (
        <LessonEditor
          lesson={lessonModal.lesson}
          courses={courses}
          defaultCourseId={lessonModal.course_id}
          defaultSectionId={lessonModal.section_id}
          onClose={() => setLessonModal(null)}
          onSaved={() => { loadDetail(expanded); reloadCourses(); }}
        />
      )}
    </div>
  );
}
